<template>
  <div class="gsl-hrm-background-container">
    <oxd-table-filter
      :filter-title="$t('general.policies')"
      :active-filter="false"
    >
      <oxd-form @submit-valid="filterItems">
        <oxd-form-row>
          <oxd-grid :cols="4" class="gsl-hrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.name"
                :label="$t('general.name')"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-if="$can.delete(`hrm_policy`)"
                v-model="filters.status"
                type="select"
                :label="$t('general.status')"
                :options="policyStatuses"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-divider />
        <oxd-form-actions>
          <oxd-button
            type="reset"
            display-type="ghost"
            :label="$t('general.reset')"
          />
          <submit-button :label="$t('general.search')" />
        </oxd-form-actions>
      </oxd-form>
    </oxd-table-filter>
    <br />
    <div class="gsl-hrm-card-container">
      <oxd-button
        v-if="$can.create(`hrm_policy`)"
        icon-name="plus"
        display-type="secondary"
        :label="$t('general.add')"
        @click="onClickAdd"
      />
      <oxd-divider v-if="$can.create(`hrm_policy`)" />

      <oxd-text v-if="items?.data?.length === 0" tag="span">
        {{ $t('general.no_records_found') }}
      </oxd-text>
      <div v-for="item in items?.data" :key="item.id">
        <div class="policy-item-container">
          <div class="policy-content">
            <div class="policy-header">
              <span class="policy-name">{{ item.name }}</span>
              <span
                class="policy-status"
                :class="{
                  active: item.status === 'Published',
                  draft: item.status === 'Draft',
                  inactive: item.status === 'Deleted',
                }"
              >
                {{ item.status }}
              </span>
            </div>
            <p class="policy-description">
              {{ item.description }}
            </p>
            <div class="policy-footer">
              <span>
                <span class="active-status">{{
                  item.active == 1 ? 'ACTIVE' : 'INACTIVE'
                }}</span>
                <span class="created-time">{{ getDisplayTime(item) }}</span>
              </span>
              <div class="policy-actions">
                <oxd-icon-button
                  v-if="$can.update(`hrm_policy`)"
                  class="icon-button"
                  name="pencil-fill"
                  @click="onClickEdit(item)"
                >
                </oxd-icon-button>
                <oxd-icon-button
                  v-if="item.status !== 'Deleted' && $can.delete(`hrm_policy`)"
                  class="icon-button delete"
                  name="trash-fill"
                  @click="onClickDelete(item)"
                >
                </oxd-icon-button>

                <oxd-button
                  v-if="item.attachment"
                  class="icon-button"
                  icon-name="cloud-download-fill"
                  display-type="text"
                  label="View/Download"
                  @click="onClickDownload(item)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showPaginator" class="gsl-hrm-bottom-container">
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
      </div>
    </div>

    <delete-confirmation ref="deleteDialog"></delete-confirmation>
  </div>
</template>

<script>
import {computed, ref} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import {navigate} from '@ohrm/core/util/helper/navigation';
import {formatTimeAgo, parseDate} from '@/core/util/helper/datefns';

const defaultFilters = {
  name: '',
  status: null,
};

export default {
  components: {
    'delete-confirmation': DeleteConfirmationDialog,
  },
  setup() {
    const policyNormalizer = (data) => {
      return data.map((item) => {
        const createUtcDate = parseDate(
          `${item.createdDate} ${item.createdTime} +00:00`,
          'yyyy-MM-dd HH:mm xxx',
        );
        const updatedUtcDate = parseDate(
          `${item.updatedDate} ${item.updatedTime} +00:00`,
          'yyyy-MM-dd HH:mm xxx',
        );
        return {
          id: item.id,
          name: item.name,
          description: item.description,
          status: item.status,
          active: item.active,
          attachment: item?.attachment?.attachFilename,
          createdAt: formatTimeAgo(createUtcDate),
          updatedAt: formatTimeAgo(updatedUtcDate),
        };
      });
    };

    const filters = ref({...defaultFilters});

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/policy/policies',
    );

    const serializedFilters = computed(() => {
      return {
        name: filters.value.name,
        status: filters.value.status?.id,
      };
    });

    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {
      query: serializedFilters,
      normalizer: policyNormalizer,
    });

    return {
      http,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: response,
      filters,
    };
  },
  data() {
    return {
      policyStatuses: [
        {id: 0, label: this.$t('general.published')},
        {id: 1, label: this.$t('general.draft')},
        {id: 2, label: this.$t('general.deleted')},
      ],
    };
  },
  methods: {
    onClickAdd() {
      navigate('/policy/savePolicies');
    },
    onClickEdit(item) {
      navigate('/policy/editPolicies/{id}', {id: item.id});
    },

    onClickDelete(item) {
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItem(item.id);
        }
      });
    },
    deleteItem(id) {
      this.isLoading = true;
      this.http
        .delete(id)
        .then(() => {
          return this.$toast.deleteSuccess();
        })
        .then(() => {
          this.isLoading = false;
          this.execQuery();
        });
    },
    onClickDownload(item) {
      const viewUrl = `${window.appGlobal.baseUrl}/policy/viewAttachment/policies/${item.id}`;
      window.open(viewUrl, '_blank');
    },
    async filterItems() {
      await this.execQuery();
    },
    getDisplayTime(item) {
      return item.createdAt !== item.updatedAt
        ? 'Updated: ' + item.updatedAt
        : 'Created: ' + item.createdAt;
    },
  },
};
</script>

<style lang="scss" scoped>
.policy {
  &-item {
    &-container {
      font-family: $oxd-font-family;
      display: flex;
      flex-direction: column;
      padding: 1rem;
      border-radius: 8px;
      box-shadow: 1px 2px 5px rgba(150, 145, 143, 0.2);
      background: #f7f7f7;
      border: 1px solid #d1d1d1;
      margin: 0.5rem;
      position: relative;
      box-shadow: 1px 2px 5px 3px rgba(150, 145, 143, 0.192);
    }
  }
  &-header {
    font-family: $oxd-font-family;
    display: flex;
    justify-content: space-between;
    font-size: 16px;
    font-weight: bold;
  }

  &-status {
    font-family: $oxd-font-family;
    font-size: 14px;
    padding: 2px 16px;
    border-radius: 12px;
    background-color: #28a745;
    color: white;

    &.inactive {
      background-color: #dc3545;
    }
    &.draft {
      background-color: #dcbd35ff;
    }
  }

  &-description {
    font-size: 14px;
    color: #555;
    margin: 0.5rem 0;
    font-family: $oxd-font-family;
  }

  &-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    color: #777;
    margin-top: 0.5rem;
    font-family: $oxd-font-family;
  }

  &-actions {
    display: flex;
    gap: 8px;
  }
}
.icon-button {
  font-size: 15px;
  font-weight: 900;
  border: 1px solid #00000044;
  box-shadow: inset 0 1px 3px #00000062;
}
.delete {
  color: #dc3545;
}
.download {
  color: #28a745;
}
.active-status {
  font-size: 12px;
  font-weight: 800;
  color: #000;
  margin-right: 15px;
  padding: 2px 16px;
  border-radius: 12px;
  background-color: #d1d1d1;
}
</style>
